// Rules behind the meal plan builder widget. Pure functions only (runs in the
// browser). Every number here comes from a source cited on the tool page:
//
// - Mifflin et al. 1990 (Am J Clin Nutr): resting energy expenditure, kcal/day
//   = 10 x kg + 6.25 x cm - 5 x age + 5 (men) or - 161 (women). Reused from
//   @/lib/workout-planner so both tools agree.
// - FAO/WHO/UNU 2004, Human energy requirements, Table 5.3: physical activity
//   level (PAL) of 1.40-1.69 for a sedentary or light activity lifestyle,
//   1.70-1.99 active or moderately active, 2.00-2.40 vigorous. Daily energy =
//   resting energy x PAL, shown as the range for the chosen lifestyle.
// - 2013 AHA/ACC/TOS guideline for overweight and obesity (Jensen et al.):
//   "a 500-kcal/d or 750-kcal/d energy deficit", or 1,200-1,500 kcal/d for
//   women and 1,500-1,800 kcal/d for men. The planner takes off 500 and never
//   goes below the lowest of those targets.
// - Dietary Guidelines for Americans 2025-2030 (realfood.gov): "1.2-1.6 grams
//   of protein per kilogram of body weight per day".
// - ISSN position stand on protein and exercise (Jager et al. 2017): 1.4-2.0
//   g/kg/day "is sufficient for most exercising individuals"; per meal 20-40 g,
//   "evenly distributed, every 3-4 h".
// - WHO healthy diet fact sheet (2026): at least 400 g of fruit and vegetables
//   a day; free sugars under 10% of energy (5% for extra benefit); salt under
//   5 g a day; saturated fat no more than 10% of energy; at least 25 g fibre.
// - WHO Global Health Observatory: adult underweight is a BMI under 18.5.

import type { Sex } from '@/lib/workout-planner'

import { restingKcalPerDay } from '@/lib/workout-planner'

export type { Sex }

export type Activity = 'low' | 'active' | 'very'
export type Goal = 'well' | 'lose' | 'gain'
export type Pattern = 'all' | 'pescatarian' | 'vegetarian' | 'vegan'
export type Avoid = 'dairy' | 'gluten' | 'egg' | 'nuts' | 'fish' | 'soy'
export type Flag = 'meat' | Avoid
export type Slot = 'breakfast' | 'lunch' | 'dinner' | 'snack'
// A place in the day. Up to two snacks, both drawn from the snack slot.
export type MealKey = 'breakfast' | 'lunch' | 'dinner' | 'snack' | 'snack2'

export interface PlannerInput {
    age: number
    weightKg: number
    heightCm: number
    sex: Sex
    activity: Activity
    goal: Goal
    pattern: Pattern
    avoid: Avoid[]
    snacks: number
    quick: boolean
    // Answered yes to a health question: no calorie cut and no protein target.
    cautious: boolean
    // Answered yes to the eating disorder question: no numbers at all.
    noNumbers: boolean
}

export interface Targets {
    kcal: [number, number]
    protein: [number, number] | null
    proteinSource: 'dga' | 'issn' | null
    perMeal: [number, number] | null
    fibre: number
    fruitVeg: number
    sugar: [number, number]
    salt: number
    satFat: number
}

export interface PlanDay {
    day: string
    meals: Partial<Record<MealKey, string>>
}

export interface MealPlan {
    targets: Targets | null
    days: PlanDay[]
    keys: MealKey[]
    notes: string[]
    lowered: boolean
}

export const SLOT_OF: Record<MealKey, Slot> = {
    breakfast: 'breakfast',
    lunch: 'lunch',
    dinner: 'dinner',
    snack: 'snack',
    snack2: 'snack'
}

// FAO/WHO/UNU 2004, Table 5.3.
export const PAL: Record<Activity, [number, number]> = {
    low: [1.4, 1.69],
    active: [1.7, 1.99],
    very: [2.0, 2.4]
}

// 2013 AHA/ACC/TOS guideline.
const DEFICIT_KCAL = 500
// The lowest standard targets in the same guideline. Without an answer for
// sex, the midpoint is used, as in the workout planner.
const FLOOR_KCAL: Record<Sex, number> = { female: 1200, male: 1500, unspecified: 1350 }

// Dietary Guidelines for Americans 2025-2030, and ISSN 2017 for people who
// train hard or want to build muscle.
const PROTEIN_DGA: [number, number] = [1.2, 1.6]
const PROTEIN_ISSN: [number, number] = [1.4, 2.0]
// ISSN 2017: 20-40 g per meal.
const PER_MEAL_G: [number, number] = [20, 40]

// WHO healthy diet fact sheet.
const FIBRE_G = 25
const FRUIT_VEG_G = 400
const FREE_SUGAR_SHARE: [number, number] = [0.05, 0.1]
const SALT_G = 5
const SAT_FAT_SHARE = 0.1
const KCAL_PER_G_SUGAR = 4
const KCAL_PER_G_FAT = 9

// WHO Global Health Observatory.
export const UNDERWEIGHT_BMI = 18.5

export const WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

const round = (value: number, step: number): number => Math.round(value / step) * step

export const bmi = (weightKg: number, heightCm: number): number => weightKg / (heightCm / 100) ** 2

export const buildTargets = (input: PlannerInput): { targets: Targets; lowered: boolean } => {
    const resting = restingKcalPerDay(input.weightKg, input.heightCm, input.age, input.sex)
    let low = resting * PAL[input.activity][0]
    let high = resting * PAL[input.activity][1]
    let lowered = false

    if (input.goal === 'lose' && !input.cautious) {
        const floor = FLOOR_KCAL[input.sex]
        low = Math.max(low - DEFICIT_KCAL, floor)
        high = Math.max(high - DEFICIT_KCAL, floor)
        lowered = true
    }

    const kcal: [number, number] = [round(low, 50), round(high, 50)]
    const mid = (kcal[0] + kcal[1]) / 2

    const useIssn = input.activity === 'very' || input.goal === 'gain'
    const perKg = useIssn ? PROTEIN_ISSN : PROTEIN_DGA
    const protein: [number, number] | null = input.cautious
        ? null
        : [round(input.weightKg * perKg[0], 5), round(input.weightKg * perKg[1], 5)]

    // Spread the day's protein over the main meals, kept inside ISSN's 20-40 g.
    const perMeal: [number, number] | null = protein
        ? [
            Math.min(Math.max(round(protein[0] / 3, 5), PER_MEAL_G[0]), PER_MEAL_G[1]),
            Math.min(Math.max(round(protein[1] / 3, 5), PER_MEAL_G[0]), PER_MEAL_G[1])
        ]
        : null

    return {
        lowered,
        targets: {
            kcal,
            protein,
            proteinSource: input.cautious ? null : useIssn ? 'issn' : 'dga',
            perMeal,
            fibre: FIBRE_G,
            fruitVeg: FRUIT_VEG_G,
            sugar: [
                round((mid * FREE_SUGAR_SHARE[0]) / KCAL_PER_G_SUGAR, 5),
                round((mid * FREE_SUGAR_SHARE[1]) / KCAL_PER_G_SUGAR, 5)
            ],
            salt: SALT_G,
            satFat: round((mid * SAT_FAT_SHARE) / KCAL_PER_G_FAT, 1)
        }
    }
}

// ----------------------------------------------------------------------------
// Meal library. Every meal follows the plate the Nutrition guide describes:
// half vegetables or fruit, a quarter protein, a quarter whole grains or
// starchy vegetables, plus a little healthy fat. Flags list what a meal
// contains so the planner can leave it out. "milk" and "yogurt" ingredients are
// written as MILK and YOGURT and switch to fortified plant versions when dairy
// is off the menu, so those meals carry no dairy flag.

export type Aisle = 'Fruit and vegetables' | 'Protein' | 'Dairy and alternatives' | 'Grains and bread' | 'Cupboard' | 'Frozen'

export interface Ingredient {
    item: string
    aisle: Aisle
}

export interface Meal {
    id: string
    slot: Slot
    name: string
    description: string
    flags: Flag[]
    quick: boolean
    ingredients: Ingredient[]
}

const veg = (item: string): Ingredient => ({ item, aisle: 'Fruit and vegetables' })
const protein = (item: string): Ingredient => ({ item, aisle: 'Protein' })
const dairy = (item: string): Ingredient => ({ item, aisle: 'Dairy and alternatives' })
const grain = (item: string): Ingredient => ({ item, aisle: 'Grains and bread' })
const cupboard = (item: string): Ingredient => ({ item, aisle: 'Cupboard' })
const frozen = (item: string): Ingredient => ({ item, aisle: 'Frozen' })

const MILK = 'MILK'
const YOGURT = 'YOGURT'

export const MEALS: Meal[] = [
    // Breakfast
    {
        id: 'overnight-oats',
        slot: 'breakfast',
        name: 'Overnight oats with berries and chia',
        description: 'Oats soaked in milk overnight, topped with berries and a spoon of chia seeds.',
        flags: [],
        quick: true,
        ingredients: [grain('Rolled oats'), dairy(MILK), frozen('Mixed berries'), cupboard('Chia seeds')]
    },
    {
        id: 'veggie-scramble',
        slot: 'breakfast',
        name: 'Scrambled eggs with spinach and tomatoes on wholegrain toast',
        description: 'Two eggs scrambled with a handful of spinach, cherry tomatoes on the side.',
        flags: ['egg', 'gluten'],
        quick: true,
        ingredients: [protein('Eggs'), veg('Spinach'), veg('Cherry tomatoes'), grain('Wholegrain bread')]
    },
    {
        id: 'tofu-scramble',
        slot: 'breakfast',
        name: 'Tofu scramble with peppers and wholegrain toast',
        description: 'Crumbled firm tofu fried with peppers, onion, turmeric and black pepper.',
        flags: ['soy', 'gluten'],
        quick: true,
        ingredients: [protein('Firm tofu'), veg('Peppers'), veg('Onions'), cupboard('Ground turmeric'), grain('Wholegrain bread')]
    },
    {
        id: 'yogurt-bowl',
        slot: 'breakfast',
        name: 'Yogurt bowl with fruit, oats and seeds',
        description: 'Plain yogurt with sliced fruit, a sprinkle of oats and pumpkin seeds.',
        flags: [],
        quick: true,
        ingredients: [dairy(YOGURT), veg('Apples or pears'), grain('Rolled oats'), cupboard('Pumpkin seeds')]
    },
    {
        id: 'pb-banana-toast',
        slot: 'breakfast',
        name: 'Peanut butter and banana on wholegrain toast',
        description: 'Wholegrain toast with no-added-sugar peanut butter, sliced banana and a glass of milk.',
        flags: ['nuts', 'gluten'],
        quick: true,
        ingredients: [grain('Wholegrain bread'), cupboard('Peanut butter, no added sugar'), veg('Bananas'), dairy(MILK)]
    },
    {
        id: 'feta-omelette',
        slot: 'breakfast',
        name: 'Spinach and feta omelette with fruit',
        description: 'A two-egg omelette with spinach and a little feta, and a piece of fruit.',
        flags: ['egg', 'dairy'],
        quick: true,
        ingredients: [protein('Eggs'), veg('Spinach'), dairy('Feta cheese'), veg('Oranges')]
    },
    {
        id: 'savoury-oats',
        slot: 'breakfast',
        name: 'Savoury oats with a fried egg and greens',
        description: 'Oats cooked in water with a pinch of pepper, topped with an egg and wilted kale.',
        flags: ['egg'],
        quick: true,
        ingredients: [grain('Rolled oats'), protein('Eggs'), veg('Kale')]
    },
    {
        id: 'chickpea-pancake',
        slot: 'breakfast',
        name: 'Chickpea flour pancake with tomato and spinach',
        description: 'A thin savoury pancake of chickpea flour and water, folded around tomato and spinach.',
        flags: [],
        quick: true,
        ingredients: [cupboard('Chickpea (gram) flour'), veg('Tomatoes'), veg('Spinach'), veg('Onions')]
    },
    {
        id: 'salmon-rye',
        slot: 'breakfast',
        name: 'Smoked salmon and avocado on rye',
        description: 'Rye bread with smashed avocado, smoked salmon and a squeeze of lemon.',
        flags: ['fish', 'gluten'],
        quick: true,
        ingredients: [grain('Rye bread'), veg('Avocados'), protein('Smoked salmon'), veg('Lemons')]
    },
    {
        id: 'buckwheat-porridge',
        slot: 'breakfast',
        name: 'Buckwheat porridge with apple and cinnamon',
        description: 'Buckwheat flakes simmered in milk with grated apple, cinnamon and seeds.',
        flags: [],
        quick: true,
        ingredients: [grain('Buckwheat flakes'), dairy(MILK), veg('Apples or pears'), cupboard('Ground cinnamon'), cupboard('Pumpkin seeds')]
    },
    {
        id: 'breakfast-burrito',
        slot: 'breakfast',
        name: 'Breakfast burrito with eggs, black beans and peppers',
        description: 'Wholegrain tortilla filled with scrambled egg, black beans, peppers and salsa.',
        flags: ['egg', 'gluten'],
        quick: false,
        ingredients: [grain('Wholegrain tortillas'), protein('Eggs'), cupboard('Tinned black beans'), veg('Peppers'), cupboard('Tomato salsa')]
    },
    {
        id: 'sweet-potato-hash',
        slot: 'breakfast',
        name: 'Sweet potato hash with beans and spinach',
        description: 'Diced sweet potato fried in olive oil with onion, white beans and spinach.',
        flags: [],
        quick: false,
        ingredients: [veg('Sweet potatoes'), veg('Onions'), cupboard('Tinned white beans'), veg('Spinach'), cupboard('Olive oil')]
    },

    // Lunch
    {
        id: 'lentil-soup',
        slot: 'lunch',
        name: 'Lentil and vegetable soup with wholegrain bread',
        description: 'A big pot of red lentils, carrots, celery and tomatoes that lasts several days.',
        flags: ['gluten'],
        quick: false,
        ingredients: [cupboard('Red lentils'), veg('Carrots'), veg('Celery'), cupboard('Tinned chopped tomatoes'), grain('Wholegrain bread')]
    },
    {
        id: 'chickpea-quinoa-salad',
        slot: 'lunch',
        name: 'Chickpea and quinoa salad with lemon tahini',
        description: 'Quinoa, chickpeas, cucumber, tomatoes and parsley with a lemon and tahini dressing.',
        flags: [],
        quick: true,
        ingredients: [grain('Quinoa'), cupboard('Tinned chickpeas'), veg('Cucumbers'), veg('Tomatoes'), veg('Parsley'), cupboard('Tahini'), veg('Lemons')]
    },
    {
        id: 'tuna-bean-salad',
        slot: 'lunch',
        name: 'Tuna and white bean salad with leafy greens',
        description: 'Tinned tuna, white beans, red onion and salad leaves with olive oil and lemon.',
        flags: ['fish'],
        quick: true,
        ingredients: [protein('Tinned tuna in water'), cupboard('Tinned white beans'), veg('Red onions'), veg('Salad leaves'), cupboard('Olive oil'), veg('Lemons')]
    },
    {
        id: 'hummus-wrap',
        slot: 'lunch',
        name: 'Hummus and roasted vegetable wholegrain wrap',
        description: 'A wholegrain wrap with hummus, roasted peppers, courgette and rocket.',
        flags: ['gluten'],
        quick: true,
        ingredients: [grain('Wholegrain tortillas'), cupboard('Hummus'), veg('Peppers'), veg('Courgettes'), veg('Rocket')]
    },
    {
        id: 'chicken-rice-bowl',
        slot: 'lunch',
        name: 'Chicken, avocado and brown rice bowl',
        description: 'Brown rice, sliced chicken, avocado, sweetcorn and shredded lettuce with salsa.',
        flags: ['meat'],
        quick: false,
        ingredients: [grain('Brown rice'), protein('Chicken breast'), veg('Avocados'), frozen('Sweetcorn'), veg('Lettuce'), cupboard('Tomato salsa')]
    },
    {
        id: 'egg-fried-rice',
        slot: 'lunch',
        name: 'Egg and vegetable fried brown rice',
        description: 'Brown rice stir-fried with egg, peas, carrots and spring onions, with a dash of soy sauce or tamari.',
        flags: ['egg', 'soy'],
        quick: true,
        ingredients: [grain('Brown rice'), protein('Eggs'), frozen('Peas'), veg('Carrots'), veg('Spring onions'), cupboard('Soy sauce or tamari')]
    },
    {
        id: 'black-bean-salad',
        slot: 'lunch',
        name: 'Black bean, corn and quinoa salad',
        description: 'Quinoa with black beans, sweetcorn, peppers, coriander and lime.',
        flags: [],
        quick: true,
        ingredients: [grain('Quinoa'), cupboard('Tinned black beans'), frozen('Sweetcorn'), veg('Peppers'), veg('Coriander'), veg('Limes')]
    },
    {
        id: 'greek-salad',
        slot: 'lunch',
        name: 'Greek salad with chickpeas and wholegrain pitta',
        description: 'Tomatoes, cucumber, olives, a little feta and chickpeas, with a wholegrain pitta.',
        flags: ['dairy', 'gluten'],
        quick: true,
        ingredients: [veg('Tomatoes'), veg('Cucumbers'), cupboard('Olives'), dairy('Feta cheese'), cupboard('Tinned chickpeas'), grain('Wholegrain pitta')]
    },
    {
        id: 'sardine-toast',
        slot: 'lunch',
        name: 'Sardines on wholegrain toast with tomato salad',
        description: 'Tinned sardines on toast with a tomato, red onion and parsley salad.',
        flags: ['fish', 'gluten'],
        quick: true,
        ingredients: [protein('Tinned sardines'), grain('Wholegrain bread'), veg('Tomatoes'), veg('Red onions'), veg('Parsley')]
    },
    {
        id: 'edamame-soba',
        slot: 'lunch',
        name: 'Tofu and edamame soba noodle salad',
        description: 'Soba noodles with tofu, edamame, shredded cabbage and carrot in a sesame dressing.',
        flags: ['soy', 'gluten'],
        quick: true,
        ingredients: [grain('Soba noodles'), protein('Firm tofu'), frozen('Edamame beans'), veg('Red cabbage'), veg('Carrots'), cupboard('Sesame oil')]
    },
    {
        id: 'turkey-sandwich',
        slot: 'lunch',
        name: 'Turkey and salad wholegrain sandwich',
        description: 'Wholegrain bread with sliced turkey, lettuce, tomato and cucumber, with fruit on the side.',
        flags: ['meat', 'gluten'],
        quick: true,
        ingredients: [grain('Wholegrain bread'), protein('Sliced cooked turkey'), veg('Lettuce'), veg('Tomatoes'), veg('Cucumbers'), veg('Apples or pears')]
    },
    {
        id: 'jacket-potato',
        slot: 'lunch',
        name: 'Baked potato with beans and a side salad',
        description: 'A microwaved potato, skin on, with reduced-salt baked beans and a green salad.',
        flags: [],
        quick: true,
        ingredients: [veg('Baking potatoes'), cupboard('Reduced-salt baked beans'), veg('Salad leaves')]
    },
    {
        id: 'dhal',
        slot: 'lunch',
        name: 'Red lentil dhal with spinach and brown rice',
        description: 'Red lentils simmered with onion, garlic, ginger and spices, spinach stirred in.',
        flags: [],
        quick: false,
        ingredients: [cupboard('Red lentils'), veg('Onions'), veg('Garlic'), veg('Ginger'), veg('Spinach'), grain('Brown rice'), cupboard('Curry spices')]
    },
    {
        id: 'cottage-cheese-plate',
        slot: 'lunch',
        name: 'Cottage cheese, crunchy vegetables and wholegrain crackers',
        description: 'A plate of cottage cheese, carrot, pepper and cucumber sticks, crackers and grapes.',
        flags: ['dairy', 'gluten'],
        quick: true,
        ingredients: [dairy('Cottage cheese'), veg('Carrots'), veg('Peppers'), veg('Cucumbers'), grain('Wholegrain crackers'), veg('Grapes')]
    },

    // Dinner
    {
        id: 'baked-salmon',
        slot: 'dinner',
        name: 'Baked salmon with roasted vegetables and potatoes',
        description: 'Salmon fillet baked on a tray with new potatoes, broccoli and red onion.',
        flags: ['fish'],
        quick: false,
        ingredients: [protein('Salmon fillets'), veg('New potatoes'), veg('Broccoli'), veg('Red onions'), cupboard('Olive oil')]
    },
    {
        id: 'chicken-stir-fry',
        slot: 'dinner',
        name: 'Chicken and vegetable stir-fry with brown rice',
        description: 'Chicken strips with peppers, broccoli and snap peas, ginger, garlic and a little soy sauce.',
        flags: ['meat', 'soy'],
        quick: false,
        ingredients: [protein('Chicken breast'), veg('Peppers'), veg('Broccoli'), veg('Sugar snap peas'), veg('Ginger'), veg('Garlic'), grain('Brown rice'), cupboard('Soy sauce or tamari')]
    },
    {
        id: 'bean-chilli',
        slot: 'dinner',
        name: 'Three-bean chilli with brown rice',
        description: 'Kidney, black and pinto beans with tomatoes, peppers and spices. Makes extra for the freezer.',
        flags: [],
        quick: false,
        ingredients: [cupboard('Tinned kidney beans'), cupboard('Tinned black beans'), cupboard('Tinned pinto beans'), cupboard('Tinned chopped tomatoes'), veg('Peppers'), veg('Onions'), grain('Brown rice'), cupboard('Chilli powder and cumin')]
    },
    {
        id: 'lentil-pasta',
        slot: 'dinner',
        name: 'Wholewheat pasta with lentil and tomato sauce',
        description: 'A rich tomato sauce with green lentils, carrot and garlic, with a side salad.',
        flags: ['gluten'],
        quick: false,
        ingredients: [grain('Wholewheat pasta'), cupboard('Tinned green lentils'), cupboard('Tinned chopped tomatoes'), veg('Carrots'), veg('Garlic'), veg('Salad leaves')]
    },
    {
        id: 'chickpea-curry',
        slot: 'dinner',
        name: 'Chickpea and spinach curry with brown rice',
        description: 'Chickpeas simmered with tomatoes, onion, curry spices and spinach.',
        flags: [],
        quick: false,
        ingredients: [cupboard('Tinned chickpeas'), cupboard('Tinned chopped tomatoes'), veg('Onions'), veg('Spinach'), grain('Brown rice'), cupboard('Curry spices')]
    },
    {
        id: 'turkey-meatballs',
        slot: 'dinner',
        name: 'Turkey meatballs in tomato sauce with wholewheat spaghetti',
        description: 'Lean turkey mince meatballs baked, then simmered in tomato sauce with courgette.',
        flags: ['meat', 'gluten'],
        quick: false,
        ingredients: [protein('Lean turkey mince'), cupboard('Tinned chopped tomatoes'), veg('Courgettes'), veg('Onions'), grain('Wholewheat spaghetti')]
    },
    {
        id: 'tofu-stir-fry',
        slot: 'dinner',
        name: 'Tofu and broccoli stir-fry with rice noodles',
        description: 'Crisp tofu cubes with broccoli, carrot and pak choi, garlic and a little soy sauce.',
        flags: ['soy'],
        quick: false,
        ingredients: [protein('Firm tofu'), veg('Broccoli'), veg('Carrots'), veg('Pak choi'), veg('Garlic'), grain('Rice noodles'), cupboard('Soy sauce or tamari')]
    },
    {
        id: 'tray-chicken',
        slot: 'dinner',
        name: 'Tray-baked chicken thighs with sweet potato and green beans',
        description: 'Skinless chicken thighs roasted with sweet potato wedges and green beans.',
        flags: ['meat'],
        quick: false,
        ingredients: [protein('Skinless chicken thighs'), veg('Sweet potatoes'), veg('Green beans'), cupboard('Olive oil')]
    },
    {
        id: 'fish-tacos',
        slot: 'dinner',
        name: 'Fish tacos with cabbage slaw',
        description: 'Baked white fish in corn tortillas with red cabbage slaw, avocado and lime.',
        flags: ['fish'],
        quick: false,
        ingredients: [protein('White fish fillets'), grain('Corn tortillas'), veg('Red cabbage'), veg('Avocados'), veg('Limes')]
    },
    {
        id: 'lentil-shepherds-pie',
        slot: 'dinner',
        name: "Lentil shepherd's pie with sweet potato topping",
        description: 'Green lentils, carrots and peas in gravy under a mashed sweet potato top.',
        flags: [],
        quick: false,
        ingredients: [cupboard('Tinned green lentils'), veg('Carrots'), frozen('Peas'), veg('Onions'), veg('Sweet potatoes'), cupboard('Low-salt vegetable stock')]
    },
    {
        id: 'omelette-salad',
        slot: 'dinner',
        name: 'Vegetable omelette with new potatoes and salad',
        description: 'A three-egg omelette with mushrooms and peppers, boiled new potatoes and a big salad.',
        flags: ['egg'],
        quick: true,
        ingredients: [protein('Eggs'), veg('Mushrooms'), veg('Peppers'), veg('New potatoes'), veg('Salad leaves')]
    },
    {
        id: 'prawn-noodles',
        slot: 'dinner',
        name: 'Prawn and vegetable rice noodles',
        description: 'Prawns stir-fried with a frozen vegetable mix, garlic, lime and rice noodles.',
        flags: ['fish'],
        quick: true,
        ingredients: [frozen('Prawns'), frozen('Stir-fry vegetable mix'), veg('Garlic'), veg('Limes'), grain('Rice noodles')]
    },
    {
        id: 'bean-quesadillas',
        slot: 'dinner',
        name: 'Bean and vegetable quesadillas with salad',
        description: 'Wholegrain tortillas filled with mashed beans, peppers, spinach and a little cheese.',
        flags: ['dairy', 'gluten'],
        quick: true,
        ingredients: [grain('Wholegrain tortillas'), cupboard('Tinned black beans'), veg('Peppers'), veg('Spinach'), dairy('Cheddar cheese'), veg('Salad leaves')]
    },
    {
        id: 'stuffed-peppers',
        slot: 'dinner',
        name: 'Quinoa and black bean stuffed peppers',
        description: 'Peppers filled with quinoa, black beans, tomatoes and spices, baked until soft.',
        flags: [],
        quick: false,
        ingredients: [veg('Peppers'), grain('Quinoa'), cupboard('Tinned black beans'), cupboard('Tinned chopped tomatoes'), cupboard('Chilli powder and cumin')]
    },
    {
        id: 'tuna-pasta',
        slot: 'dinner',
        name: 'Wholewheat pasta with tuna, peas and lemon',
        description: 'Wholewheat pasta tossed with tuna, peas, spinach, lemon and olive oil.',
        flags: ['fish', 'gluten'],
        quick: true,
        ingredients: [grain('Wholewheat pasta'), protein('Tinned tuna in water'), frozen('Peas'), veg('Spinach'), veg('Lemons'), cupboard('Olive oil')]
    },
    {
        id: 'black-bean-tacos',
        slot: 'dinner',
        name: 'Black bean tacos with avocado and slaw',
        description: 'Warm spiced black beans in corn tortillas with avocado, cabbage slaw and lime.',
        flags: [],
        quick: true,
        ingredients: [cupboard('Tinned black beans'), grain('Corn tortillas'), veg('Avocados'), veg('Red cabbage'), veg('Limes'), cupboard('Chilli powder and cumin')]
    },
    {
        id: 'fajita-bowl',
        slot: 'dinner',
        name: 'Bean and vegetable fajita bowl',
        description: 'Peppers and onions fried with fajita spices, over brown rice with pinto beans and salsa.',
        flags: [],
        quick: true,
        ingredients: [veg('Peppers'), veg('Onions'), cupboard('Tinned pinto beans'), grain('Brown rice'), cupboard('Tomato salsa'), cupboard('Chilli powder and cumin')]
    },

    // Snacks
    {
        id: 'apple-pb',
        slot: 'snack',
        name: 'Apple with peanut butter',
        description: 'A sliced apple with a tablespoon of no-added-sugar peanut butter.',
        flags: ['nuts'],
        quick: true,
        ingredients: [veg('Apples or pears'), cupboard('Peanut butter, no added sugar')]
    },
    {
        id: 'yogurt-berries',
        slot: 'snack',
        name: 'Plain yogurt with berries',
        description: 'A pot of plain yogurt with a handful of berries.',
        flags: [],
        quick: true,
        ingredients: [dairy(YOGURT), frozen('Mixed berries')]
    },
    {
        id: 'hummus-sticks',
        slot: 'snack',
        name: 'Hummus with carrot and pepper sticks',
        description: 'A few spoons of hummus with crunchy vegetable sticks.',
        flags: [],
        quick: true,
        ingredients: [cupboard('Hummus'), veg('Carrots'), veg('Peppers')]
    },
    {
        id: 'nuts-fruit',
        slot: 'snack',
        name: 'Unsalted nuts and a piece of fruit',
        description: 'A small handful of unsalted nuts with an orange or a pear.',
        flags: ['nuts'],
        quick: true,
        ingredients: [cupboard('Unsalted mixed nuts'), veg('Oranges')]
    },
    {
        id: 'boiled-eggs',
        slot: 'snack',
        name: 'Boiled eggs with cherry tomatoes',
        description: 'Two boiled eggs, made ahead, with cherry tomatoes.',
        flags: ['egg'],
        quick: true,
        ingredients: [protein('Eggs'), veg('Cherry tomatoes')]
    },
    {
        id: 'roasted-chickpeas',
        slot: 'snack',
        name: 'Roasted chickpeas',
        description: 'Chickpeas roasted with paprika until crunchy. A batch lasts the week.',
        flags: [],
        quick: true,
        ingredients: [cupboard('Tinned chickpeas'), cupboard('Smoked paprika')]
    },
    {
        id: 'crackers-cheese',
        slot: 'snack',
        name: 'Wholegrain crackers with cheese and grapes',
        description: 'A few wholegrain crackers with a matchbox-sized piece of cheese and grapes.',
        flags: ['dairy', 'gluten'],
        quick: true,
        ingredients: [grain('Wholegrain crackers'), dairy('Cheddar cheese'), veg('Grapes')]
    },
    {
        id: 'edamame-snack',
        slot: 'snack',
        name: 'Steamed edamame beans',
        description: 'A bowl of edamame beans from the freezer, steamed in a few minutes.',
        flags: ['soy'],
        quick: true,
        ingredients: [frozen('Edamame beans')]
    },
    {
        id: 'banana-milk',
        slot: 'snack',
        name: 'Banana and a glass of milk',
        description: 'A banana with a glass of milk.',
        flags: [],
        quick: true,
        ingredients: [veg('Bananas'), dairy(MILK)]
    },
    {
        id: 'pear-seeds',
        slot: 'snack',
        name: 'Pear with pumpkin seeds',
        description: 'A pear with a small handful of pumpkin seeds.',
        flags: [],
        quick: true,
        ingredients: [veg('Apples or pears'), cupboard('Pumpkin seeds')]
    }
]

const MEALS_BY_ID = new Map(MEALS.map((meal) => [meal.id, meal]))

export const mealById = (id: string | undefined): Meal | undefined => (id ? MEALS_BY_ID.get(id) : undefined)

// What each eating pattern leaves out.
const PATTERN_EXCLUDES: Record<Pattern, Flag[]> = {
    all: [],
    pescatarian: ['meat'],
    vegetarian: ['meat', 'fish'],
    vegan: ['meat', 'fish', 'egg', 'dairy']
}

export const excludedFlags = (pattern: Pattern, avoid: Avoid[]): Flag[] =>
    Array.from(new Set<Flag>([...PATTERN_EXCLUDES[pattern], ...avoid]))

const noDairy = (pattern: Pattern, avoid: Avoid[]): boolean => pattern === 'vegan' || avoid.includes('dairy')

// MILK and YOGURT become plant versions when dairy is off the menu.
export const ingredientName = (item: string, pattern: Pattern, avoid: Avoid[]): string => {
    const plant = noDairy(pattern, avoid)
    if (item === MILK) return plant ? 'Fortified unsweetened plant milk' : 'Milk'
    if (item === YOGURT) return plant ? 'Fortified unsweetened plant yogurt' : 'Plain yogurt'
    return item
}

// Meal names and descriptions say "milk" and "yogurt"; with dairy off the menu
// they read "plant milk" and "plant yogurt", matching the shopping list.
export const mealText = (text: string, pattern: Pattern, avoid: Avoid[]): string =>
    noDairy(pattern, avoid)
        ? text.replace(/\b(milk|yogurt)\b/gi, (word) => (word[0] === word[0].toUpperCase() ? `Plant ${word.toLowerCase()}` : `plant ${word}`))
        : text

// Meals that fit the answers, for one slot. When "quick meals only" leaves
// fewer than three options, longer meals are added back so the week still
// has variety.
export const optionsFor = (slot: Slot, pattern: Pattern, avoid: Avoid[], quick: boolean): Meal[] => {
    const excluded = excludedFlags(pattern, avoid)
    const fits = MEALS.filter((meal) => meal.slot === slot && !meal.flags.some((flag) => excluded.includes(flag)))
    if (!quick) return fits

    const fast = fits.filter((meal) => meal.quick)
    return fast.length >= 3 ? fast : [...fast, ...fits.filter((meal) => !meal.quick)]
}

// Spreads the options over the week: each option is used once before any
// repeats, and the same meal never lands on two days in a row when there is
// another choice. Deterministic, so the same answers give the same week.
const spread = (options: Meal[], offset: number): string[] => {
    if (options.length === 0) return WEEK.map(() => '')

    const days: string[] = []
    for (let index = 0; index < WEEK.length; index += 1) {
        days.push(options[(index + offset) % options.length].id)
    }
    return days
}

export const keysFor = (snacks: number): MealKey[] => {
    const keys: MealKey[] = ['breakfast', 'lunch', 'dinner']
    if (snacks >= 1) keys.push('snack')
    if (snacks >= 2) keys.push('snack2')
    return keys
}

export const buildMealPlan = (input: PlannerInput): MealPlan => {
    const keys = keysFor(input.snacks)
    const notes: string[] = []
    const options = (slot: Slot): Meal[] => optionsFor(slot, input.pattern, input.avoid, input.quick)

    // Different starting points, so lunch and dinner do not run in step, and a
    // second snack differs from the first.
    const byKey: Record<MealKey, string[]> = {
        breakfast: spread(options('breakfast'), 0),
        lunch: spread(options('lunch'), 2),
        dinner: spread(options('dinner'), 1),
        snack: spread(options('snack'), 0),
        snack2: spread(options('snack'), 3)
    }

    const days: PlanDay[] = WEEK.map((day, index) => {
        const meals: Partial<Record<MealKey, string>> = {}
        for (const key of keys) {
            if (byKey[key][index]) meals[key] = byKey[key][index]
        }
        return { day, meals }
    })

    const { targets, lowered } = buildTargets(input)

    if (input.noNumbers) {
        notes.push('This plan has no calorie or protein numbers on purpose. Regular, varied meals matter more than counting. A doctor or registered dietitian can help you build a plan that feels safe for you.')
    } else if (input.cautious) {
        notes.push('Because of your health answers, this plan does not cut calories or set a protein target. Your needs may be different, so please take it to your doctor, midwife or a registered dietitian before you change how you eat.')
    }
    if (input.pattern === 'vegan') {
        notes.push('Vitamin B12 comes mainly from animal foods, so a vegan diet needs fortified foods or a supplement. Ask a pharmacist or doctor which suits you.')
    }
    if (input.avoid.includes('gluten')) {
        notes.push('Avoiding gluten: choose oats, stock, sauces and tamari labelled gluten-free. If you have coeliac disease, ask your doctor whether oats suit you.')
    }
    if (input.goal === 'gain' && !input.cautious && !input.noNumbers) {
        notes.push('To gain weight, eat toward the top of your calorie range and keep the snacks in. If you are losing weight without trying, see a doctor.')
    }

    return {
        targets: input.noNumbers ? null : targets,
        days,
        keys,
        notes,
        lowered: lowered && !input.noNumbers
    }
}

// The next meal in the same slot that fits the answers and is not already on
// that day. Returns null when there is nothing else to offer.
export const nextOption = (current: string, slot: Slot, input: Pick<PlannerInput, 'pattern' | 'avoid' | 'quick'>, takenToday: string[]): string | null => {
    const options = optionsFor(slot, input.pattern, input.avoid, input.quick)
    if (options.length < 2) return null

    const start = options.findIndex((meal) => meal.id === current)
    for (let step = 1; step < options.length; step += 1) {
        const candidate = options[(start + step + options.length) % options.length]
        if (!takenToday.includes(candidate.id)) return candidate.id
    }
    return null
}

export interface ShoppingGroup {
    aisle: Aisle
    items: { name: string; meals: number }[]
}

const AISLE_ORDER: Aisle[] = ['Fruit and vegetables', 'Protein', 'Dairy and alternatives', 'Grains and bread', 'Frozen', 'Cupboard']

// One list for the week, grouped by aisle, with how many meals use each item.
export const shoppingList = (days: PlanDay[], pattern: Pattern, avoid: Avoid[]): ShoppingGroup[] => {
    const counts = new Map<string, { aisle: Aisle; meals: number }>()

    for (const day of days) {
        for (const id of Object.values(day.meals)) {
            const meal = mealById(id)
            if (!meal) continue
            for (const ingredient of meal.ingredients) {
                const name = ingredientName(ingredient.item, pattern, avoid)
                const entry = counts.get(name)
                if (entry) entry.meals += 1
                else counts.set(name, { aisle: ingredient.aisle, meals: 1 })
            }
        }
    }

    return AISLE_ORDER
        .map((aisle) => ({
            aisle,
            items: Array.from(counts.entries())
                .filter(([, entry]) => entry.aisle === aisle)
                .map(([name, entry]) => ({ name, meals: entry.meals }))
                .sort((a, b) => a.name.localeCompare(b.name))
        }))
        .filter((group) => group.items.length > 0)
}
